import { NetworkStatus, useQuery, useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  Badge,
  Button,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  DataTable,
  DataTableColumnHeader,
  NewVerifiedBeneficiaryModal,
  PaymentIdentifierLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  StatusUI,
  Txt,
  toast,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { currencySchema, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Suspense, useState } from "react";
import { PiPlus } from "react-icons/pi";
import { RiBuilding2Line, RiFilter3Line, RiPriceTagLine } from "react-icons/ri";
import { z } from "zod";
import logoBlueOnWhite from "~/assets/brand/logoBlueOnWhite.svg";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import {
  beneficiaryAccountKindValues,
  beneficiaryAccountStatusValues,
  currencyValues,
} from "~/gql/client/enumValues";
import type {
  AdminBeneficiaryAccountsQuery,
  BeneficiaryAccountsPageFilter,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

graphql(`
  fragment AdminBeneficiaryAccountsListItem on BeneficiaryAccount {
    id
    status
    kind
    organisation {
      id
      name
    }
    businessEntity {
      id
      name
    }
    currency
    ...BeneficiaryAssetCardFields
    paymentIdentifiers {
      id
      ...PaymentIdentifierComponent
    }
  }
`);

export const Route = createFileRoute("/__auth/beneficiary-accounts")({
  head: () => opsHead("Beneficiary Accounts"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const navigate = useNavigate();
  const { useSearchParams } = useRouter();

  const [newVerifiedBeneModalOpen, setNewVerifiedBeneModalOpen] =
    useState(false);

  const beneficiaryAccountsListDocument = graphql(`
    query AdminBeneficiaryAccounts($after: String, $first: Int!, $filter: BeneficiaryAccountsPageFilter!) {
      beneficiaryAccounts(after: $after, first: $first, filter: $filter) {
        beneficiaryAccounts {
          id
          ...AdminBeneficiaryAccountsListItem
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  `);

  const [filter, setFilter] = useQueryPersistedStateWithSchema({
    useSearchParams,
    schema: z.object({
      organisationIds: z.array(z.string()).optional().nullable(),
      businessEntityIds: z.array(z.string()).optional().nullable(),
      currencies: z.array(currencySchema).nullable().optional(),
      status: z
        .array(z.enum(beneficiaryAccountStatusValues))
        .optional()
        .nullable(),
      kinds: z
        .array(z.enum(beneficiaryAccountKindValues))
        .optional()
        .nullable(),
    }),
    defaultState: {},
  });

  const baseInput = {
    first: 20,
    filter,
  };

  const { fetchMore, loading, networkStatus, data } = useQuery(
    beneficiaryAccountsListDocument,
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const beneficiaryAccounts =
    data?.beneficiaryAccounts?.beneficiaryAccounts ?? [];

  const nextCursor = data?.beneficiaryAccounts?.pageInfo.hasNextPage
    ? (data.beneficiaryAccounts?.pageInfo.endCursor ?? undefined)
    : undefined;

  const { data: pageData } = useSuspenseQuery(
    graphql(`
      query AdminBeneficiariesPage {
        organisations {
          id
          name
          businessEntities {
            id
            name
          }
        }
      }
    `),
  );

  const organisationOptions =
    pageData.organisations?.flatMap((o) => ({
      value: o.id,
      label: o.name,
    })) ?? [];

  const businessEntityOptions =
    pageData.organisations
      ?.flatMap((org) =>
        org.businessEntities?.map((be) => ({
          value: be.id,
          label: be.name,
        })),
      )
      .filter((opt) => opt !== undefined) ?? [];

  return (
    <AdminPageContainer testId="admin-beneficiary-accounts">
      <AdminPageHeader
        title="Beneficiary Accounts"
        subtitle="Destination accounts for payouts"
        buttons={
          <Button
            variant="primary"
            leftIcon={<PiPlus />}
            onClick={() => setNewVerifiedBeneModalOpen(true)}
          >
            New Beneficiary Account
          </Button>
        }
      />
      <div>
        <FilterBar
          filterValue={filter}
          onFilterChange={(values) => {
            setFilter({
              ...values,
            });
          }}
          organisationOptions={organisationOptions}
          businessEntityOptions={businessEntityOptions}
        />
        <DataTable
          data={beneficiaryAccounts}
          columns={columns}
          isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
          linkTo={(row) => `/beneficiary-accounts/${row.original.id}`}
          manualSorting={true}
          manualPagination={true}
          maxMultiSortColCount={1}
          hidePagination={true}
          pageSize={10_000}
          className="overflow-hidden border"
          initialColumnVisibility={{
            // filter only columns
            businessEntityId: false,
            currency: false, // currency show in the AssetCard
          }}
        />
      </div>

      <div className="mt-4 flex w-full flex-row justify-center">
        {nextCursor ? (
          <Button
            onClick={() =>
              fetchMore({ variables: { ...baseInput, after: nextCursor } })
            }
            variant="outline"
            loading={loading || networkStatus === NetworkStatus.fetchMore}
          >
            Load more
          </Button>
        ) : (
          !!beneficiaryAccounts.length && (
            <p className="text-secondary text-sm">No more data</p>
          )
        )}
      </div>

      <NewVerifiedBeneficiaryModal
        open={newVerifiedBeneModalOpen}
        onOpenChange={setNewVerifiedBeneModalOpen}
        logoBlueOnWhiteSrc={logoBlueOnWhite}
        onSuccess={(id) => {
          setNewVerifiedBeneModalOpen(false);
          toast({
            kind: "info",
            title: "Beneficiary Account Created",
            description: "Beneficiary account requires review",
            autoHide: true,
          });
          void navigate({ to: `/beneficiary-accounts/${id}` });
        }}
        activeOrganisation={null}
      />
    </AdminPageContainer>
  );
}

type BeneficiaryRow = NonNullable<
  NonNullable<
    AdminBeneficiaryAccountsQuery["beneficiaryAccounts"]
  >["beneficiaryAccounts"]
>[number];

const columns: ColumnDef<BeneficiaryRow>[] = [
  {
    id: "status",
    enableSorting: false,
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      return <StatusUI tooltip iconOnly status={row.original.status} />;
    },
  },
  {
    id: "client",
    accessorFn: (row) => row.organisation?.id,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Client" />
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const { organisation, businessEntity } = row.original;
      return (
        <div className="flex flex-col items-start">
          <Txt variant="name">{organisation?.name ?? "-"}</Txt>
          {businessEntity && <Txt variant="caption">{businessEntity.name}</Txt>}
        </div>
      );
    },
    filterFn: (row, _id, value) =>
      value.includes(row.original.organisation?.id),
  },
  {
    id: "id",
    accessorFn: (row) => row.id,
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Account" />
    ),
    cell: ({ row }) => {
      return (
        <AccountCard
          account={row.original}
          subText={["CURRENCY", "BALANCE_FULL", "NICKNAME"]}
        />
      );
    },
    filterFn: (row, _id, value) => value.includes(row.original.id),
  },
  {
    accessorKey: "identifiers",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Identifiers" />
    ),
    cell: ({ row }) => {
      const friendlyIdentifier = row.original.friendlyIdentifier;

      // PaymentIdentifierLabel varies by ABA network, so dedupe on that too
      const identifiersByKind = new Map<
        string,
        NonNullable<typeof row.original.paymentIdentifiers>[number]
      >();
      for (const p of row.original.paymentIdentifiers ?? []) {
        let kind: string = p.__typename;
        if (p.__typename === "UsAbaPaymentIdentifier") {
          kind = `${p.__typename}_${p.validNetworks.toSorted().join(",")}`;
        }
        identifiersByKind.set(kind, p);
      }

      return (
        <div className="flex flex-col gap-2">
          <Txt>{friendlyIdentifier}</Txt>
          <div className="flex gap-2">
            {Array.from(identifiersByKind).map(([kind, p]) => {
              return (
                <Badge key={kind} variant="outline">
                  <PaymentIdentifierLabel paymentIdentifier={p} />
                </Badge>
              );
            })}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "kind",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Kind" />
    ),
    cell: ({ row }) => {
      return prettyPrintEnum(row.original.kind);
    },
  },
  // Filter only columns
  {
    id: "businessEntityId",
    accessorFn: (row) => row.businessEntity?.id,
    filterFn: (row, _id, value) =>
      value.includes(row.original.businessEntity?.id),
  },
  {
    id: "currency",
    accessorFn: (row) => row.currency,
    enableSorting: false,
    filterFn: (row, _id, value) => value.includes(row.original.currency),
  },
];

function FilterBar({
  filterValue,
  onFilterChange,
  organisationOptions,
  businessEntityOptions,
}: {
  filterValue: BeneficiaryAccountsPageFilter;
  onFilterChange: (_: BeneficiaryAccountsPageFilter) => void;
  organisationOptions: {
    value: string;
    label: React.ReactNode;
  }[];
  businessEntityOptions: {
    value: string;
    label: React.ReactNode;
  }[];
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("status");
  const filterCount =
    ((filterValue?.organisationIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.businessEntityIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.status?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.kinds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.currencies?.length ?? 0) > 0 ? 1 : 0);

  const resetFilter = () => {
    onFilterChange({
      ...filterValue,
      organisationIds: undefined,
      businessEntityIds: undefined,
      status: undefined,
      kinds: undefined,
      currencies: undefined,
    });
  };

  const statusOptions = beneficiaryAccountStatusValues.map((k) => ({
    value: k,
    label: <span>{k}</span>,
  }));
  const kindOptions = beneficiaryAccountKindValues.map((k) => ({
    value: k,
    label: <span>{k}</span>,
  }));
  const currencyOptions = currencyValues.map((currency) => {
    return {
      label: currency,
      value: currency,
    };
  });

  return (
    <div className="flex w-full flex-row gap-2">
      <Popover open={open} onOpenChange={(open) => setOpen(open)}>
        <PopoverTrigger asChild>
          <Button
            leftIcon={<RiFilter3Line />}
            variant="outline"
            size="sm"
            className="mb-4 h-8 w-fit text-primary"
          >
            Filter{" "}
            {filterCount > 0 && (
              <span className="ml-2">
                <span className="text-info">({filterCount})</span>
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex h-[500px] w-[760px] p-0" align="start">
          <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("status")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Status</div>
                {(filterValue.status?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("kind")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Kind</div>
                {(filterValue.kinds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("organisationId")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Organisation</div>
                {(filterValue.organisationIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("businessEntityId")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Business entity</div>
                {(filterValue.businessEntityIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-primary text-primary"
              onClick={resetFilter}
            >
              Reset all filters
            </Button>
          </div>
          <div className="flex-1">
            {path === "status" && (
              <ControlledFacetedFilterControl
                options={statusOptions}
                selectedValues={filterValue.status ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    status: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "kind" && (
              <ControlledFacetedFilterControl
                options={kindOptions}
                selectedValues={filterValue.kinds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    kinds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "organisationId" && (
              <ControlledFacetedFilterControl
                options={organisationOptions}
                selectedValues={filterValue.organisationIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    organisationIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "businessEntityId" && (
              <ControlledFacetedFilterControl
                options={businessEntityOptions}
                selectedValues={filterValue.businessEntityIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    businessEntityIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "currencies" && (
              <ControlledFacetedFilterControl
                options={currencyOptions}
                selectedValues={filterValue.currencies ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    currencies: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>

      <ControlledFactedFilterSummary
        title="Status"
        options={statusOptions}
        selectedValues={filterValue.status ?? []}
        onClick={() => {
          setPath("status");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Kind"
        options={kindOptions}
        selectedValues={filterValue.kinds ?? []}
        onClick={() => {
          setPath("kind");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Organisation"
        options={organisationOptions}
        selectedValues={filterValue.organisationIds ?? []}
        onClick={() => {
          setPath("organisationId");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Business entity"
        options={businessEntityOptions}
        selectedValues={filterValue.businessEntityIds ?? []}
        onClick={() => {
          setPath("businessEntityId");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Currency"
        options={currencyOptions}
        selectedValues={filterValue.currencies ?? []}
        onClick={() => {
          setPath("currencies");
          setOpen(true);
        }}
      />

      {filterCount > 0 && (
        <Button
          rightIcon={<X />}
          variant="ghost"
          size="sm"
          onClick={resetFilter}
        >
          Reset
        </Button>
      )}
    </div>
  );
}
